/*
 * VideoUtils.js
 *
 * Utility helpers to fetch a raw H.264 (Annex‑B) stream, decode every frame
 * with **broadway-decoder**, optionally **center‑crop** them to a desired
 * width/height, and convert them into WebGL textures.
 *
 * API
 *   export async function extractFramesFromH264(gl, url, cropW, cropH)
 *
 *     gl      : WebGLRenderingContext
 *     url     : path / data URI of a .h264 elementary stream
 *     cropW   : desired output width  (pixels). If null ⇒ use source width
 *     cropH   : desired output height (pixels). If null ⇒ use source height
 *
 * Returns: Promise<WebGLTexture[]> – textures in decode order
 *
 * Dependencies
 *   npm i broadway-decoder
 */

import {Decoder} from "broadway-player";

// ───────────────────────────────────────────────────────────── FETCH ─┐
/** Fetch a binary resource and return it as an ArrayBuffer. */
export async function fetchArrayBuffer(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    return res.arrayBuffer();
}
// ────────────────────────────────────────────────────────────────────┘

// ───────────────────────────────────────────────────────────── WEBGL ─┐
/**
 * Create a WebGL RGB texture from a raw RGB buffer (Uint8Array).
 */
export function createTextureFromFrame(gl, buffer, width, height) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        width,
        height,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        buffer
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return tex;
}
// ────────────────────────────────────────────────────────────────────┘

// ─────────────────────────────────────────────────────────── HELPERS ─┐
/**
 * Center‑crop an RGB buffer.
 *
 * @param {Uint8Array} src    RGB888 source
 * @param {number} srcW       source width
 * @param {number} srcH       source height
 * @param {number} cropW      target width (<= srcW)
 * @param {number} cropH      target height(<= srcH)
 * @returns {Uint8Array}      cropped RGB buffer (cropW*cropH*3)
 */
function cropBuffer(src, srcW, srcH, cropW, cropH) {
    // If no cropping requested, return original view (no copy)
    if (!cropW || !cropH || (cropW === srcW && cropH === srcH)) return src;

    const dst = new Uint8Array(cropW * cropH * 4);

    for (let y = 0; y < cropH; y++) {
        const srcRow = y * srcW * 4;
        const dstRow = y * cropW * 4;
        dst.set(src.subarray(srcRow, srcRow + cropW * 4), dstRow);
    }
    return dst;
}

function initDecoder() {
    return new Decoder({ rgb: true }); // outputs RGB888
}
// ────────────────────────────────────────────────────────────────────┘

// ──────────────────────────────────────────────────── MAIN EXPORT ─┐
/**
 * Decode an H.264 Annex‑B stream to WebGL textures, cropping each frame.
 *
 * @param {WebGLRenderingContext} gl
 * @param {string} url
 * @param {number|null} cropW
 * @param {number|null} cropH
 */
export async function extractFramesFromH264(gl, url, cropW = null, cropH = null) {
    const data = new Uint8Array(await fetchArrayBuffer(url));
    const decoder = initDecoder();
    const textures = [];
    let srcW = 0,
        srcH = 0;

    decoder.onPictureDecoded = (buffer, w, h) => {
        srcW = w;
        srcH = h;

        const rgb = new Uint8Array(buffer);
        const cropped = cropBuffer(rgb, srcW, srcH, cropW || srcW, cropH || srcH);

        const tex = createTextureFromFrame(gl, cropped, cropW || srcW, cropH || srcH);
        textures.push(tex);
    };

    // Split Annex‑B by 0x000001 start codes (quick & dirty NALU separation)
    let last = 0;
    for (let i = 2; i < data.length - 3; i++) {
        if (data[i] === 0x00 && data[i + 1] === 0x00 && data[i + 2] === 0x01) {
            decoder.decode(data.subarray(last, i));
            last = i;
        }
    }
    decoder.decode(data.subarray(last)); // final NALU

    return textures;
}
// ─────────────────────────────────────────────────────────────────┘

/*
 * FFmpeg – convert MP4 → raw H.264 stream
 * ---------------------------------------
 * ffmpeg -i input.mp4 -c:v libx264 -preset veryfast -profile:v baseline \
 *        -level 3.0 -pix_fmt yuv420p -an -f h264 output.h264
 */
